// Blog: Firestore collection `blogPosts`. Public can read published posts; only admins write (see firestore.rules).
// Post body is Markdown. renderMarkdown() escapes everything first, so posts can never inject scripts.
import { db, storage } from "@/firebase";
import {
  collection, doc, getDoc, getDocs, query, where, limit as qLimit,
  addDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { ref as sref, uploadBytes, getDownloadURL } from "firebase/storage";

export const BLOG_BASE = "https://muzilly.com";
export const blogShareUrl = (slug) => `${BLOG_BASE}/s/blog/${slug}`;

export const CATEGORIES = ["Product", "Artists", "Podcasts", "Music videos", "Community", "Company"];

const ms = (t) => (!t ? 0 : t.toMillis ? t.toMillis() : t.seconds ? t.seconds * 1000 : +new Date(t));
const norm = (d) => ({ id: d.id, ...d.data() });

export const slugify = (s) =>
  String(s || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export const stripMarkdown = (md) =>
  String(md || "").replace(/```[\s\S]*?```/g, " ").replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").replace(/[#>*_`~-]+/g, " ").replace(/\s+/g, " ").trim();

export const readMinutes = (md) => Math.max(1, Math.round(stripMarkdown(md).split(" ").filter(Boolean).length / 220));
export const autoExcerpt = (md, n = 170) => { const t = stripMarkdown(md); return t.length > n ? t.slice(0, n - 1).trimEnd() + "…" : t; };
export const fmtPostDate = (t) => {
  const v = ms(t); return v ? new Date(v).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";
};

// ── Markdown → safe HTML ────────────────────────────────────────────────────────
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const safeUrl = (u) => (/^(https?:\/\/|\/(?!\/)|mailto:)/i.test(u.trim()) ? esc(u.trim()) : "#");

function inline(text) {
  let t = esc(text);
  t = t.replace(/`([^`]+)`/g, "<code>$1</code>");
  t = t.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_, a, u) => `<img src="${safeUrl(u)}" alt="${a}" loading="lazy">`);
  t = t.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, a, u) => {
    const ext = /^https?:/i.test(u) && !u.includes("muzilly.com");
    return `<a href="${safeUrl(u)}"${ext ? ' target="_blank" rel="noopener"' : ""}>${a}</a>`;
  });
  t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>").replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  return t;
}

/** Headings (#, ##, ###), paragraphs, lists, quotes, code blocks, images, dividers, links, bold, italic. */
export function renderMarkdown(md) {
  const lines = String(md || "").replace(/\r/g, "").split("\n");
  const out = []; let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (!l.trim()) { i++; continue; }
    if (/^```/.test(l)) {
      const buf = []; i++;
      while (i < lines.length && !/^```/.test(lines[i])) buf.push(lines[i++]);
      i++; out.push(`<pre><code>${esc(buf.join("\n"))}</code></pre>`); continue;
    }
    const h = l.match(/^(#{1,3})\s+(.*)$/);
    if (h) { const n = h[1].length + 1; out.push(`<h${n}>${inline(h[2])}</h${n}>`); i++; continue; }
    if (/^(-{3,}|\*{3,})\s*$/.test(l)) { out.push("<hr>"); i++; continue; }
    const img = l.match(/^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/);
    if (img) {
      out.push(`<figure><img src="${safeUrl(img[2])}" alt="${esc(img[1])}" loading="lazy">${img[1] ? `<figcaption>${esc(img[1])}</figcaption>` : ""}</figure>`);
      i++; continue;
    }
    if (/^>\s?/.test(l)) {
      const buf = []; while (i < lines.length && /^>\s?/.test(lines[i])) buf.push(lines[i++].replace(/^>\s?/, ""));
      out.push(`<blockquote>${inline(buf.join(" "))}</blockquote>`); continue;
    }
    if (/^\s*[-*]\s+/.test(l)) {
      const buf = []; while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) buf.push(`<li>${inline(lines[i++].replace(/^\s*[-*]\s+/, ""))}</li>`);
      out.push(`<ul>${buf.join("")}</ul>`); continue;
    }
    if (/^\s*\d+\.\s+/.test(l)) {
      const buf = []; while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) buf.push(`<li>${inline(lines[i++].replace(/^\s*\d+\.\s+/, ""))}</li>`);
      out.push(`<ol>${buf.join("")}</ol>`); continue;
    }
    const buf = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|```|>|\s*[-*]\s|\s*\d+\.\s|!\[)/.test(lines[i])) buf.push(lines[i++]);
    if (!buf.length) { buf.push(lines[i++]); }
    out.push(`<p>${inline(buf.join(" "))}</p>`);
  }
  return out.join("\n");
}

// ── Public reads ────────────────────────────────────────────────────────────────
/** Newest first. Sorted here so no composite index is needed. */
export async function listPublished(max = 30) {
  const snap = await getDocs(query(collection(db, "blogPosts"), where("status", "==", "published"), qLimit(60)));
  return snap.docs.map(norm).filter((p) => ms(p.publishedAt) <= Date.now() + 60000)
    .sort((a, b) => ms(b.publishedAt) - ms(a.publishedAt)).slice(0, max);
}

export async function getPublishedBySlug(slug) {
  const snap = await getDocs(query(collection(db, "blogPosts"), where("status", "==", "published"), where("slug", "==", slug), qLimit(1)));
  return snap.empty ? null : norm(snap.docs[0]);
}

// ── Admin ───────────────────────────────────────────────────────────────────────
export async function listAll() {
  const snap = await getDocs(collection(db, "blogPosts"));
  return snap.docs.map(norm).sort((a, b) => ms(b.updatedAt) - ms(a.updatedAt));
}
export async function getPost(id) { const s = await getDoc(doc(db, "blogPosts", id)); return s.exists() ? norm(s) : null; }

async function slugTaken(slug, exceptId) {
  const snap = await getDocs(query(collection(db, "blogPosts"), where("slug", "==", slug), qLimit(3)));
  return snap.docs.some((d) => d.id !== exceptId);
}

/** Creates or updates. `p.status` is "draft" or "published". Returns the post id. */
export async function savePost(id, p, author) {
  const slug = slugify(p.slug || p.title);
  if (!p.title || !p.title.trim()) throw new Error("Add a title first.");
  if (!slug) throw new Error("The web address (slug) can't be empty.");
  if (await slugTaken(slug, id)) throw new Error(`Another post already uses “${slug}”. Change the web address.`);
  if (p.status === "published" && !String(p.body || "").trim()) throw new Error("Write something before publishing.");
  const data = {
    title: p.title.trim(), slug, subtitle: (p.subtitle || "").trim(),
    excerpt: (p.excerpt || "").trim() || autoExcerpt(p.body),
    body: p.body || "", coverUrl: p.coverUrl || "", coverAlt: (p.coverAlt || "").trim(),
    category: p.category || "", tags: (p.tags || []).slice(0, 8),
    authorName: (p.authorName || "").trim() || (author && author.name) || "Muzilly",
    authorId: (author && author.uid) || p.authorId || "",
    readMinutes: readMinutes(p.body), status: p.status === "published" ? "published" : "draft",
    featured: !!p.featured, updatedAt: serverTimestamp(),
  };
  if (data.status === "published") data.publishedAt = p.publishedAt ? Timestamp.fromMillis(ms(p.publishedAt)) : serverTimestamp();
  else data.publishedAt = null;
  if (id) { await setDoc(doc(db, "blogPosts", id), data, { merge: true }); return id; }
  data.createdAt = serverTimestamp();
  return (await addDoc(collection(db, "blogPosts"), data)).id;
}

export async function setStatus(id, status) {
  await updateDoc(doc(db, "blogPosts", id), {
    status, updatedAt: serverTimestamp(), ...(status === "published" ? { publishedAt: serverTimestamp() } : { publishedAt: null }),
  });
}
export const removePost = (id) => deleteDoc(doc(db, "blogPosts", id));

export async function uploadBlogImage(file) {
  if (!file || !/^image\//.test(file.type)) throw new Error("Choose an image file.");
  if (file.size > 8 * 1024 * 1024) throw new Error("Images must be under 8 MB.");
  const name = `${Date.now()}_${file.name.replace(/[^\w.-]+/g, "_")}`;
  const r = sref(storage, `blog/${name}`);
  await uploadBytes(r, file, { contentType: file.type });
  return getDownloadURL(r);
}